import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Bug, Plus } from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/misc';
import { Avatar } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { useMoveWorkItem, useProjects, useSprints, useWorkBoard, useWorkItems } from './api';
import { WorkItemDialog } from './work-item-dialog';
import { WorkItemSheet } from './work-item-sheet';
import {
  WORK_ITEM_PRIORITY_LABELS,
  WORK_ITEM_PRIORITY_VARIANT,
  WORK_ITEM_STATUS_LABELS,
  WORK_ITEM_TYPE_COLOUR,
  WORK_ITEM_TYPE_LABELS,
} from './labels';
import type { WorkItem, WorkItemStatus } from './types';

export function WorkBoardPage() {
  const [params, setParams] = useSearchParams();
  const projectId = params.get('projectId') ?? '';
  const sprintId = params.get('sprintId') ?? '';
  const view = params.get('view') ?? 'board';

  const [creating, setCreating] = useState(false);
  const [openItemId, setOpenItemId] = useState<string | null>(null);

  const { data: projects } = useProjects({ limit: 100, status: 'ACTIVE' });
  const { data: sprints = [] } = useSprints(projectId || undefined);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Work board"
        description="Committed work by status. The backlog is its own view — nothing is on the board until it is in a sprint."
        actions={
          <Can permission="project.task.create">
            <Button disabled={!projectId} onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New item
            </Button>
          </Can>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select
          value={projectId}
          onChange={(event) => setParam('projectId', event.target.value)}
          className="h-9 w-auto min-w-[14rem]"
        >
          <option value="">Choose a project</option>
          {(projects?.data ?? []).map((project) => (
            <option key={project.id} value={project.id}>
              {project.code} — {project.name}
            </option>
          ))}
        </Select>

        {projectId && (
          <Select
            value={sprintId}
            onChange={(event) => setParam('sprintId', event.target.value)}
            className="h-9 w-auto min-w-[11rem]"
          >
            <option value="">All active sprints</option>
            {sprints.map((sprint) => (
              <option key={sprint.id} value={sprint.id}>
                {sprint.name}
              </option>
            ))}
          </Select>
        )}

        <div className="ml-auto flex gap-1 rounded-lg border p-0.5">
          {(['board', 'backlog'] as const).map((value) => (
            <button
              key={value}
              onClick={() => setParam('view', value)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm capitalize transition-colors',
                view === value ? 'bg-secondary font-medium' : 'text-muted-foreground',
              )}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      {!projectId ? (
        <Card>
          <CardContent className="py-14 text-center text-sm text-muted-foreground">
            Choose a project to see its board.
          </CardContent>
        </Card>
      ) : view === 'board' ? (
        <Board projectId={projectId} sprintId={sprintId || undefined} onOpen={setOpenItemId} />
      ) : (
        <Backlog projectId={projectId} onOpen={setOpenItemId} />
      )}

      {creating && (
        <WorkItemDialog
          projectId={projectId}
          sprintId={sprintId || undefined}
          onClose={() => setCreating(false)}
        />
      )}
      {openItemId && <WorkItemSheet id={openItemId} onClose={() => setOpenItemId(null)} />}
    </div>
  );
}

function Board({
  projectId,
  sprintId,
  onOpen,
}: {
  projectId: string;
  sprintId?: string;
  onOpen: (id: string) => void;
}) {
  const { data, isLoading } = useWorkBoard(projectId, sprintId);
  const move = useMoveWorkItem();
  const [dragging, setDragging] = useState<WorkItem | null>(null);
  const [over, setOver] = useState<WorkItemStatus | null>(null);
  const [blocked, setBlocked] = useState<{ message: string; details?: { message: string }[] } | null>(
    null,
  );

  if (isLoading) return <TableSkeleton columns={5} />;
  if (!data) return null;

  const drop = async (status: WorkItemStatus) => {
    setOver(null);
    if (!dragging || dragging.status === status) return;
    const item = dragging;
    setDragging(null);

    try {
      await move.mutateAsync({ id: item.id, status });
      setBlocked(null);
    } catch (caught) {
      // The server names the field that is missing, so say which one rather
      // than silently snapping the card back.
      const error = caught as { message?: string; details?: { message: string }[] };
      setBlocked({ message: error.message ?? 'That move is not allowed', details: error.details });
    }
  };

  return (
    <div className="min-h-0 flex-1">
      {blocked && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">{blocked.message}</p>
            {blocked.details?.map((detail) => (
              <p key={detail.message} className="text-xs">
                {detail.message}
              </p>
            ))}
          </div>
          <button
            className="ml-auto text-xs underline"
            onClick={() => setBlocked(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="scrollbar-thin flex gap-3 overflow-x-auto pb-4">
        {data.columns.map((column) => (
          <div
            key={column.status}
            className={cn(
              'flex w-72 shrink-0 flex-col rounded-lg border bg-muted/30 transition-colors',
              over === column.status && 'border-primary bg-primary/5',
            )}
            onDragOver={(event) => {
              event.preventDefault();
              setOver(column.status);
            }}
            onDragLeave={() => setOver((current) => (current === column.status ? null : current))}
            onDrop={() => void drop(column.status)}
          >
            <div className="flex items-baseline justify-between border-b px-3 py-2.5">
              <span className="text-sm font-medium">
                {WORK_ITEM_STATUS_LABELS[column.status]}
                <span className="ml-2 text-xs text-muted-foreground">{column.count}</span>
              </span>
              <span className="tabular text-xs text-muted-foreground">
                {column.estimateHours > 0 ? `${column.estimateHours}h` : ''}
                {column.unestimated > 0 && (
                  <span className="ml-1.5 text-amber-600" title="Items with no estimate">
                    +{column.unestimated}?
                  </span>
                )}
              </span>
            </div>

            <div className="scrollbar-thin flex-1 space-y-2 overflow-y-auto p-2">
              {column.items.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  onOpen={onOpen}
                  onDragStart={() => setDragging(item)}
                  onDragEnd={() => setDragging(null)}
                />
              ))}
              {column.items.length === 0 && (
                <p className="py-6 text-center text-xs text-muted-foreground">Nothing here</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Backlog({ projectId, onOpen }: { projectId: string; onOpen: (id: string) => void }) {
  const { data, isLoading } = useWorkItems({ projectId, backlogOnly: true, limit: 100 });

  if (isLoading) return <TableSkeleton columns={4} />;

  const items = data?.data ?? [];
  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-14 text-center text-sm text-muted-foreground">
          The backlog is empty — everything is in a sprint.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="divide-y p-0">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => onOpen(item.id)}
            className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
          >
            <span className={cn('shrink-0 text-xs font-semibold', WORK_ITEM_TYPE_COLOUR[item.itemType])}>
              {WORK_ITEM_TYPE_LABELS[item.itemType]}
            </span>
            <span className="shrink-0 font-mono text-xs text-muted-foreground">{item.key}</span>
            <span className="min-w-0 flex-1 truncate text-sm">{item.title}</span>
            {item.estimateHours !== null && (
              <span className="tabular shrink-0 text-xs text-muted-foreground">
                {item.estimateHours}h
              </span>
            )}
            <Badge variant={WORK_ITEM_PRIORITY_VARIANT[item.priority]} className="shrink-0">
              {WORK_ITEM_PRIORITY_LABELS[item.priority]}
            </Badge>
            {item.assignee && (
              <Avatar name={item.assignee.name} className="h-6 w-6 shrink-0 text-[10px]" />
            )}
          </button>
        ))}
      </CardContent>
    </Card>
  );
}

function ItemCard({
  item,
  onOpen,
  onDragStart,
  onDragEnd,
}: {
  item: WorkItem;
  onOpen: (id: string) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(item.id)}
      className="cursor-pointer rounded-lg border bg-background p-3 shadow-sm transition-shadow hover:shadow-md"
    >
      <div className="flex items-center gap-1.5">
        {item.itemType === 'BUG' ? (
          <Bug className="h-3.5 w-3.5 text-destructive" />
        ) : (
          <span className={cn('text-[10px] font-bold uppercase', WORK_ITEM_TYPE_COLOUR[item.itemType])}>
            {WORK_ITEM_TYPE_LABELS[item.itemType]}
          </span>
        )}
        <span className="font-mono text-[11px] text-muted-foreground">{item.key}</span>
        {item.addedMidSprint && (
          <span
            className="ml-auto rounded bg-amber-100 px-1.5 text-[10px] font-medium text-amber-700"
            title="Added after the sprint started"
          >
            added
          </span>
        )}
      </div>

      <p className="mt-1.5 line-clamp-3 text-sm">{item.title}</p>

      <div className="mt-2.5 flex items-center gap-2">
        <Badge variant={WORK_ITEM_PRIORITY_VARIANT[item.priority]} className="text-[10px]">
          {WORK_ITEM_PRIORITY_LABELS[item.priority]}
        </Badge>
        {item.estimateHours !== null && (
          <span className="tabular text-[11px] text-muted-foreground">{item.estimateHours}h</span>
        )}
        {item.loggedHours > 0 && (
          <span
            className={cn(
              'tabular text-[11px]',
              item.estimateHours !== null && item.loggedHours > item.estimateHours
                ? 'font-medium text-amber-600'
                : 'text-muted-foreground',
            )}
            title="Time logged"
          >
            · {item.loggedHours}h logged
          </span>
        )}
        {item.assignee && (
          <Avatar name={item.assignee.name} className="ml-auto h-6 w-6 text-[10px]" />
        )}
      </div>
    </div>
  );
}
